// Simple event system for auth-related events
// This allows services to notify the app when authentication fails

const AUTH_EXPIRED_EVENT = 'auth:expired';
// 2FA (#333): the backend answers `401 {error, code: 'mfa_required'}` to ANY
// authenticated request whose Firebase token was minted WITHOUT the second
// factor while the account has 2FA enrolled (a device signed in before 2FA was
// turned on). That is not an expired token — refreshing it yields another
// single-factor token — so it gets its own event and the App signs the user
// out at once instead of entering the refresh-then-retry recovery.
const MFA_REQUIRED_EVENT = 'auth:mfa-required';
export const MFA_REQUIRED_CODE = 'mfa_required';

// Survives the sign-out (sessionStorage, not the `omnis:` localStorage keys the
// sign-out wipes) so the login page can say WHY the user is back there.
const MFA_SIGNOUT_KEY = 'pai:mfa-signed-out';

/** True for a thrown httpError (`err.status` 401 + `err.body.code`) or a parsed 401 body. */
export const isMfaRequired = (errOrBody) => {
    if (!errOrBody) return false;
    if (errOrBody.status != null && errOrBody.status !== 401) return false;
    const body = errOrBody.body && typeof errOrBody.body === 'object' ? errOrBody.body : errOrBody;
    return body?.code === MFA_REQUIRED_CODE;
};

export const authEvents = {
    // Emit when a 401 is received from any API call
    emitAuthExpired: () => {
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    },

    // Subscribe to auth expired events
    onAuthExpired: (callback) => {
        window.addEventListener(AUTH_EXPIRED_EVENT, callback);
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, callback);
    },

    // A 401 `mfa_required` (#333) — sign out now, no token-refresh retry.
    emitMfaRequired: () => {
        window.dispatchEvent(new CustomEvent(MFA_REQUIRED_EVENT));
    },

    onMfaRequired: (callback) => {
        window.addEventListener(MFA_REQUIRED_EVENT, callback);
        return () => window.removeEventListener(MFA_REQUIRED_EVENT, callback);
    },
};

/** Remember that this sign-out was forced by 2FA (read once by LoginPage). */
export const markMfaSignOut = () => {
    try { sessionStorage.setItem(MFA_SIGNOUT_KEY, '1'); } catch (_) { /* private mode */ }
};

/** Read-and-clear the marker above. */
export const consumeMfaSignOut = () => {
    try {
        const v = sessionStorage.getItem(MFA_SIGNOUT_KEY);
        if (v) sessionStorage.removeItem(MFA_SIGNOUT_KEY);
        return !!v;
    } catch (_) {
        return false;
    }
};

/**
 * Does this 401 Response carry `code: 'mfa_required'`? Reads a CLONE, so the
 * caller can still consume the body for its own error. Never throws.
 */
export async function responseIsMfaRequired(response) {
    if (!response || response.status !== 401 || typeof response.clone !== 'function') return false;
    try {
        const body = await response.clone().json();
        return isMfaRequired(body);
    } catch (_) {
        return false;
    }
}

/**
 * The services' one 401 hook: `mfa_required` → the sign-out event, anything
 * else → the existing auth-expired (refresh-then-sign-out) event. A response
 * whose body cannot be peeked keeps the old synchronous behaviour.
 */
export function reportUnauthorized(response) {
    if (!response || response.status !== 401) return;
    if (typeof response.clone !== 'function') {
        authEvents.emitAuthExpired();
        return;
    }
    responseIsMfaRequired(response).then((mfa) => {
        if (mfa) authEvents.emitMfaRequired();
        else authEvents.emitAuthExpired();
    });
}
